import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '@minecraft.icon';
import { FlexRow, H2Text } from '@minecraft.atoms';
import { DiscountTypeEnum, PlanTermEnum } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { publishBasicMembershipSelectionEvent, publishPurchaseFlowEvent } from '@blocs.marketing-events';
import { WindowWidthContext } from '@minecraft.utils';
import { LAST_REACTIVATION_MODAL_AUTO_OPENED_DATE, QUERY_PARAMS } from '@blocs.auth';
import { DesignSystemTextColor } from '@minecraft.themes';
import { useRouter } from '@blocs.router';
import { PAYWALL_STEPS, QueryParamsPlanTerms } from './enums/paywall';
import { PlanType } from './usePlans';
import { BillingCodes, MediaPurchase, PlanCode } from './enums/plans';
import { PaywallLocation } from './enums/PaywallLocation';
import { usePaywallQueryParams } from './usePaywallQueryParams';
import { PaywallEvents } from './enums/events';
import { useRegistrationLocation } from './useRegistrationLocation';
import { usePaywallExperiments } from './usePaywallExperiments';
import { UpgradeModalExperimentGroups } from './enums/experimentGroups';
import { GetTitleParams, UsePaywall, UsePaywallProps, UsePaywallState } from './types';

const PREMIUM_ANNUAL_LEVEL = 50;

function isPremiumLocation(paywallLocation?: PaywallLocation) {
  switch (paywallLocation) {
    case PaywallLocation.AUDITIONS:
    case PaywallLocation.SUBMISSION_TRENDS:
    case PaywallLocation.ROLE_TRACKER:
    case PaywallLocation.DEEPLINK:
    case PaywallLocation.CREDITS:
    case PaywallLocation.SKILLS:
    case PaywallLocation.PROFILE_MEDIA:
    case PaywallLocation.DISCOUNT_MODAL:
    case PaywallLocation.ACTIVE_CARDLESS_TRIAL_MODAL:
    case PaywallLocation.EXPIRED_CARDLESS_TRIAL_MODAL:
    case PaywallLocation.HEADSHOTS_FREE:
      return true;
    default:
      return false;
  }
}

function getTerm(isAnnual?: boolean) {
  return isAnnual ? PlanTermEnum.Annual : PlanTermEnum.Monthly;
}

// Group the annual and monthly plans
const groupPlans = ({ plans, isAnnual }: { plans: PlanType[]; isAnnual?: boolean }): PlanType[] => {
  return plans.reduce((acc, plan) => {
    if (plan.term.code === getTerm(isAnnual) || plan.billingCode === BillingCodes.registration) {
      acc.push(plan);
    }
    return acc;
  }, []);
};

const initialState: UsePaywallState = {
  step: PAYWALL_STEPS.INITIAL,
  selectedPlan: null,
  isAnnual: false,
};

const TitleWrapper: FC<{ title: string; color?: DesignSystemTextColor }> = ({ title, color }) => {
  const isMobile = WindowWidthContext.useIsMobile();
  return (
    <FlexRow alignItems="center" gap="3" alignContent="center">
      {!isMobile && (
        <FlexRow justifyContent="flex-end" alignItems="center">
          <Icon name="rocket-outline" />
        </FlexRow>
      )}
      <H2Text className="cn_max-w-md" lineHeight="expanded" fontWeight="semibold" color={color || 'default'}>
        {title}
      </H2Text>
    </FlexRow>
  );
};

const getTitleForStep = ({
  step,
  location,
  isReactivate,
  t,
  customTitle,
  modalVariant,
  isModal,
}: {
  step: PAYWALL_STEPS;
  location: PaywallLocation;
  isReactivate: boolean;
  t: (key: string) => string;
  customTitle?: string;
  modalVariant?: UpgradeModalExperimentGroups;
  isModal?: boolean;
}) => {
  const isLifestyleUpgradeModal = modalVariant === UpgradeModalExperimentGroups.VariantB && isModal;
  if (step === PAYWALL_STEPS.INITIAL) {
    if (isLifestyleUpgradeModal) {
      return (
        <H2Text lineHeight="expanded" fontWeight="semibold" as="span" color="inverted">
          {t('common:paywall.title.lifestyle')}
        </H2Text>
      );
    }
    if (customTitle) {
      return (
        <H2Text lineHeight="expanded" fontWeight="semibold" as="span" color="default">
          {customTitle}
        </H2Text>
      );
    }
    if (isReactivate)
      return (
        <TitleWrapper
          title={t('common:paywall.title.reactivate')}
          color={isLifestyleUpgradeModal ? 'inverted' : 'default'}
        />
      );
    if (location === PaywallLocation.SIGNUP) return t('common:paywall.title.registration');
    if ([PaywallLocation.MEMBERSHIP, PaywallLocation.GLOBAL_NAV].includes(location))
      return t('common:paywall.title.membership');
    if (location === PaywallLocation.TALENT_SCOUT) return t('common:paywall.title.upgradeTalentScout');
    if (location === PaywallLocation.THRIVE_UPSELL) return t('common:paywall.title.upgradeThrive');
    if (
      [
        PaywallLocation.MEDIA_BANK,
        PaywallLocation.MEDIA_LIBRARY_UPLOAD,
        PaywallLocation.MEDIA_REQUEST_UPLOAD,
        PaywallLocation.OVERAGE_BANNER,
        PaywallLocation.EDIT_PROFILE_UPLOAD,
        PaywallLocation.CUSTOMIZE_SUBMISSION_UPLOAD,
      ].includes(location)
    ) {
      return t('common:paywall.title.upgradeUnlimitedMedia');
    }
    if (
      [PaywallLocation.CUSTOM_URL_UPSELL, PaywallLocation.OVERAGE_MODAL, PaywallLocation.OVERAGE_UPSELL_MODAL].includes(
        location
      )
    ) {
      return (
        <TitleWrapper
          title={
            location === PaywallLocation.OVERAGE_MODAL || location === PaywallLocation.OVERAGE_UPSELL_MODAL
              ? t('common:paywall.title.membership')
              : t('common:paywall.title.upgradeCustomURL')
          }
          color="default"
        />
      );
    }
    return (
      <TitleWrapper
        title={t('common:paywall.title.upgradeMembership')}
        color={isLifestyleUpgradeModal ? 'inverted' : 'default'}
      />
    );
  }
  if (step === PAYWALL_STEPS.CHECKOUT) return t('common:creditCardForm.completePurchase');
  return '';
};

const useSelectedPlanEffect = ({
  selectedPlan,
  step,
  paywallLocation,
}: {
  selectedPlan: PlanType;
  step: PAYWALL_STEPS;
  paywallLocation: PaywallLocation;
}) => {
  useEffect(() => {
    if (selectedPlan?.price && step !== PAYWALL_STEPS.SUCCESS) {
      if (selectedPlan.billingCode === BillingCodes.registration) {
        publishBasicMembershipSelectionEvent();
      }
      const isMediaPurchase = selectedPlan?.billingCode === MediaPurchase.alacarte;
      const purchaseItems = [selectedPlan].map((plan) => ({
        name: `${plan.title} ${plan.term.code} ${plan.canRenew ? 'Renewal' : ''}`,
        code: `${plan.billingCode}${plan.canRenew ? '_renewal' : ''}`,
        price: Number(plan.price),
      }));

      publishPurchaseFlowEvent(PaywallEvents.selectItem, {
        location: paywallLocation,
        purchase: {
          items: isMediaPurchase ? purchaseItems : [],
          subscriptions: !isMediaPurchase ? purchaseItems : [],
          currency: { code: selectedPlan.currencyCode },
        },
      });
    }
  }, [selectedPlan, paywallLocation, step]);
};

const usePremiumLocationEffect = ({
  locationIsPremium,
  setStep,
  isRegistration,
  isSinglePageUpgradeModal,
  isSingleClickReactivation,
  isModal,
}: {
  locationIsPremium: boolean;
  isRegistration: boolean;
  setStep: (T: PAYWALL_STEPS) => void;
  isSinglePageUpgradeModal: boolean;
  isSingleClickReactivation: boolean;
  isModal: boolean;
}) => {
  useEffect(() => {
    if (locationIsPremium || isRegistration || (isSinglePageUpgradeModal && !isSingleClickReactivation)) {
      setStep(PAYWALL_STEPS.CHECKOUT);
    }
  }, [locationIsPremium, setStep, isRegistration, isSinglePageUpgradeModal, isSingleClickReactivation, isModal]);
};

const useSingleClickReactivationEffect = ({
  isSingleClickReactivation,
  daysUntilAutoOpenIn,
  showBillingPopup,
}: {
  isSingleClickReactivation: boolean;
  daysUntilAutoOpenIn?: number;
  showBillingPopup: (data: { location?: PaywallLocation }) => void;
}) => {
  const { pathname } = useRouter();
  const lastOpened = sessionStorage.getItem(LAST_REACTIVATION_MODAL_AUTO_OPENED_DATE);
  const lastOpenedDate = lastOpened ? new Date(lastOpened) : null;
  const millisecondsInADay = 24 * 60 * 60 * 1000;
  const daysSinceLastOpened = (new Date().getTime() - (lastOpenedDate?.getTime() || 0)) / millisecondsInADay;

  useEffect(() => {
    if (isSingleClickReactivation && daysSinceLastOpened >= daysUntilAutoOpenIn && pathname !== '/default-redirect') {
      showBillingPopup({ location: PaywallLocation.GLOBAL_NAV });
      sessionStorage.setItem(LAST_REACTIVATION_MODAL_AUTO_OPENED_DATE, new Date().toString());
    }
  }, [isSingleClickReactivation, showBillingPopup, daysSinceLastOpened, daysUntilAutoOpenIn, pathname]);
};

const getInitialPlanTerm = ({
  currentPlan,
  isSinglePageUpgradeModal,
  defaultAnnualPlan,
}: {
  currentPlan: { level: number; term: { code: string } };
  isSinglePageUpgradeModal: boolean;
  defaultAnnualPlan: boolean;
}) => {
  const deepLinkPlanTerm: string = localStorage.getItem(QUERY_PARAMS.planTerm as string)?.toLowerCase();
  const validatedDeepLinkPlanTerm = Object.values(QueryParamsPlanTerms).includes(
    deepLinkPlanTerm as QueryParamsPlanTerms
  )
    ? deepLinkPlanTerm
    : null;

  // if the DEFAULT PLAN ANNUAL feature flag is true set annual to true
  if (defaultAnnualPlan) return true;

  // if there is a plan term set into localstorage from deeplink query params
  // use that value to determine the initial plan term
  if (validatedDeepLinkPlanTerm) {
    return validatedDeepLinkPlanTerm === QueryParamsPlanTerms.Annual;
  }

  // if current plan is premium and the single page upgrade modal is enabled and the modal is open, set the initial plan term to annual
  if (currentPlan?.level === 40 && isSinglePageUpgradeModal) {
    return true;
  }

  // if current plan is basic, essential, or otherwise less than a Premium plan, it defaults to m2m
  if (currentPlan?.level < PREMIUM_ANNUAL_LEVEL) {
    return false;
  }

  // set the initial plan term to match the user's current plan term if they
  // are on a premium (or higher level) plan
  return currentPlan?.term?.code === PlanTermEnum.Annual;
};

export const usePaywall = ({
  startingStep = PAYWALL_STEPS.INITIAL,
  closeBillingPopup,
  paywallLocation,
  plans,
  selectedPlan,
  setSelectedPlan = () => null,
  mediaIds,
  isModal = false,
  currentPlan,
  modalVariant,
  previousSubscriptionPlan,
  showBillingPopup,
  discountType,
}: UsePaywallProps): UsePaywall => {
  const { t } = useTranslation();
  const { customPaywallTitle } = usePaywallExperiments();
  const { isRegistration } = useRegistrationLocation({ paywallLocation });
  const isAnnualBilling = currentPlan?.term?.code === PlanTermEnum.Annual;
  const isSinglePageExperimentEnabled = modalVariant === UpgradeModalExperimentGroups.VariantA;
  const isSinglePageUpgradeModal = isSinglePageExperimentEnabled && isModal && Boolean(paywallLocation);
  // the feature flag returns a number representing the number of days after which the Single Click Reactivation paywall should auto open
  const singleClickReactivationFeatureFlagValue: number = useFeatureExperiment(FEATURE_FLAGS.SINGLE_CLICK_REACTIVATION);
  const isSingleClickReactivation = Boolean(singleClickReactivationFeatureFlagValue);
  const defaultAnnualPlan =
    useFeatureExperiment(FEATURE_FLAGS.WEB_DEFAULT_UPGRADE_MODAL_TO_ANNUAL) && !isSingleClickReactivation;
  const isReppedRegDiscount = discountType === DiscountTypeEnum.ReppedRegistrationDiscount;

  const [step, setStep] = useState<PAYWALL_STEPS>(startingStep);
  const [isAnnual, setIsAnnual] = useState<boolean>(
    getInitialPlanTerm({
      currentPlan,
      isSinglePageUpgradeModal: isSinglePageExperimentEnabled,
      defaultAnnualPlan,
    })
  );
  const locationIsPremium = isPremiumLocation(paywallLocation);
  const isAlacartePurchaseEnabled = useFeatureExperiment(FEATURE_FLAGS.ALACARTE_MEDIA_PURCHASE);
  const isPremiumPlan = useCallback(
    (plan) => (locationIsPremium ? plan?.code === PlanCode.Premium : true),
    [locationIsPremium]
  );
  const orderByOrder = useCallback((a, b) => a.order - b.order, []);

  const availablePlans = useMemo(() => {
    const plansWithCurrentPlan = currentPlan ? [...plans, currentPlan] : plans;
    return plansWithCurrentPlan.filter(isPremiumPlan).sort(orderByOrder);
  }, [plans, currentPlan, isPremiumPlan, orderByOrder]);

  const getHighestAvailablePlan = useCallback(() => {
    const termCode = isAnnual ? PlanTermEnum.Annual : PlanTermEnum.Monthly;
    return availablePlans
      ?.filter((plan) => plan.term?.code === termCode)
      .sort((a, b) => b.level - a.level)
      .shift();
  }, [availablePlans, isAnnual]);

  const getHighestAvailableAnnualPlan = useCallback(() => {
    return availablePlans
      ?.filter((plan) => plan.term?.code === PlanTermEnum.Annual)
      .sort((a, b) => b.level - a.level)
      .shift();
  }, [availablePlans]);

  const getPreviousSubscriptionPlan = useCallback((): PlanType | null => {
    if (!previousSubscriptionPlan) return null;
    const previousPlan = availablePlans?.find((plan) => plan.billingCode === previousSubscriptionPlan);
    return previousPlan || null;
  }, [availablePlans, previousSubscriptionPlan]);

  const groupedPlans = useMemo(() => {
    return groupPlans({ plans: availablePlans, isAnnual });
  }, [availablePlans, isAnnual]);

  const updateIsAnnual = useCallback(
    (isAnnualTerm: boolean) => {
      if (availablePlans.length <= 1) return;
      const newTerm = getTerm(isAnnualTerm);
      const newSelectedPlan = availablePlans.find(
        (plan) => plan.code === selectedPlan?.code && newTerm === plan.term.code
      );
      setIsAnnual(isAnnualTerm);
      setSelectedPlan(newSelectedPlan);
    },
    [availablePlans, selectedPlan, setSelectedPlan]
  );

  const onNext = () => {
    const isLastStep = step === PAYWALL_STEPS.SUCCESS;

    if (!isLastStep) {
      const nextStep = Number(step) + 1;
      setStep(nextStep);
    }
  };

  const getTitle = useCallback(
    ({ paywallLocation: location, isHidden = false, isReactivate }: GetTitleParams = {}): string | React.ReactNode => {
      if (isHidden) return '';
      return getTitleForStep({
        step,
        location,
        isReactivate,
        t,
        customTitle: customPaywallTitle,
        modalVariant,
        isModal,
      });
    },
    [step, t, customPaywallTitle, modalVariant, isModal]
  );

  const resetStep = useCallback(() => {
    if (isSinglePageUpgradeModal && !isSingleClickReactivation) {
      setStep(PAYWALL_STEPS.CHECKOUT);
    } else {
      setStep(initialState.step);
    }
    setIsAnnual(
      getInitialPlanTerm({
        currentPlan,
        isSinglePageUpgradeModal,
        defaultAnnualPlan,
      })
    );
    setSelectedPlan(initialState.selectedPlan);
  }, [defaultAnnualPlan, currentPlan, isSinglePageUpgradeModal, isSingleClickReactivation, setSelectedPlan]);

  const onBack = useCallback(() => {
    switch (step) {
      case PAYWALL_STEPS.SUCCESS:
        setStep(PAYWALL_STEPS.CHECKOUT);
        break;
      case PAYWALL_STEPS.CHECKOUT:
        setStep(PAYWALL_STEPS.INITIAL);
        break;
      case PAYWALL_STEPS.INITIAL:
      default:
        closeBillingPopup();
        break;
    }
  }, [step, closeBillingPopup]);

  const skipToLastStep = useCallback(() => {
    setStep(PAYWALL_STEPS.SUCCESS);
  }, []);

  const moveToCheckoutStep = useCallback(() => {
    setStep(PAYWALL_STEPS.CHECKOUT);
  }, []);

  useSelectedPlanEffect({ selectedPlan, step, paywallLocation });
  usePremiumLocationEffect({
    locationIsPremium,
    setStep,
    isRegistration,
    isSinglePageUpgradeModal,
    isSingleClickReactivation: Boolean(isSingleClickReactivation),
    isModal,
  });
  usePaywallQueryParams({ step, paywallLocation });
  useSingleClickReactivationEffect({
    daysUntilAutoOpenIn: singleClickReactivationFeatureFlagValue,
    isSingleClickReactivation,
    showBillingPopup,
  });

  useEffect(() => {
    // whenever the current plan changes, re-initialize the plan term value
    setIsAnnual(
      getInitialPlanTerm({
        currentPlan,
        isSinglePageUpgradeModal: isSinglePageExperimentEnabled,
        defaultAnnualPlan,
      })
    );
  }, [currentPlan, isAnnualBilling, isSinglePageExperimentEnabled, isModal, paywallLocation, defaultAnnualPlan]);
  useEffect(() => {
    const freePlan = plans.find((plan) => plan.code === PlanCode.Free);
    if (!selectedPlan) {
      if (isReppedRegDiscount) {
        setSelectedPlan(getHighestAvailableAnnualPlan());
      } else if (freePlan && isRegistration) {
        setSelectedPlan(freePlan);
      } else if (isSingleClickReactivation) {
        setSelectedPlan(getPreviousSubscriptionPlan());
      } else if (locationIsPremium || isAlacartePurchaseEnabled || isRegistration || isSinglePageUpgradeModal) {
        setSelectedPlan(getHighestAvailablePlan());
      }
    }
  }, [
    locationIsPremium,
    availablePlans,
    selectedPlan,
    setSelectedPlan,
    currentPlan,
    startingStep,
    getHighestAvailablePlan,
    getPreviousSubscriptionPlan,
    getHighestAvailableAnnualPlan,
    isSinglePageUpgradeModal,
    isAlacartePurchaseEnabled,
    isSingleClickReactivation,
    isRegistration,
    isReppedRegDiscount,
    plans,
  ]);

  useEffect(() => {
    if (isAlacartePurchaseEnabled && mediaIds?.length > 0) {
      setStep(PAYWALL_STEPS.CHECKOUT);
    }
    if (selectedPlan?.term?.code === PlanTermEnum.Annual) {
      setIsAnnual(true);
    }
  }, [isAlacartePurchaseEnabled, mediaIds, paywallLocation, selectedPlan]);

  return {
    step,
    getTitle,
    onBack,
    onNext,
    selectedPlan,
    startingStep,
    resetStep,
    setSelectedPlan,
    isAnnual,
    setIsAnnual: updateIsAnnual,
    groupedPlans,
    skipToLastStep,
    moveToCheckoutStep,
  };
};
